import {Button, TextInput} from '@primer/react'
import {useState} from 'react'
import {MarkdownRenderer} from '@github-ui/copilot-markdown'
import {
  ChoiceButton,
  ChoiceButtonGroup,
  ChoiceButtonInput,
} from '@github-ui/agent-sessions-interface/components/ChoiceButton'
import {UserChoiceCard} from '@github-ui/agent-sessions-interface/components/UserChoiceCard'

export const EMPTY_CHOICES: string[] = []

export interface SimpleAskUserDisplayProps {
  question: string
  choices: string[]
  multiSelect: boolean
  allowFreeform: boolean
  recommendedChoice?: string
  onChoose?: (choice: string) => void
  onSubmitChoices?: (choices: string[]) => void
  onFreeform?: (text: string) => void
  onSkip?: () => void
  disabled: boolean
  /** When true, hides container chrome (for embedding in another container) */
  embedded?: boolean
}

export function SimpleAskUserDisplay({
  question,
  choices,
  multiSelect,
  allowFreeform,
  recommendedChoice,
  onChoose,
  onSubmitChoices,
  onFreeform,
  onSkip,
  disabled,
  embedded = false,
}: SimpleAskUserDisplayProps) {
  const [freeformValue, setFreeformValue] = useState('')
  const [showFreeform, setShowFreeform] = useState(false)
  const [selectedChoices, setSelectedChoices] = useState<Set<string>>(() => new Set())

  const handleFreeformSubmit = () => {
    if (disabled || !onFreeform || !freeformValue.trim()) return
    onFreeform(freeformValue.trim())
  }

  const toggleChoice = (choice: string) => {
    setSelectedChoices(prev => {
      const next = new Set(prev)
      if (next.has(choice)) {
        next.delete(choice)
      } else {
        next.add(choice)
      }
      return next
    })
  }

  const isFreeformOnly = choices.length === 0 && allowFreeform

  return (
    <UserChoiceCard
      title={<MarkdownRenderer markdown={question} />}
      embedded={embedded}
      footerButtons={
        <>
          {multiSelect && (
            <Button
              variant="primary"
              onClick={() => onSubmitChoices?.([...selectedChoices])}
              disabled={disabled || selectedChoices.size === 0}
            >
              Submit
            </Button>
          )}
          {(showFreeform || isFreeformOnly) && (
            <Button variant="primary" onClick={handleFreeformSubmit} disabled={disabled || !freeformValue.trim()}>
              Send
            </Button>
          )}
          {onSkip && (
            <Button variant="default" onClick={onSkip} disabled={disabled}>
              Skip
            </Button>
          )}
        </>
      }
    >
      {isFreeformOnly ? (
        <TextInput
          placeholder="Type your answer..."
          value={freeformValue}
          onChange={e => setFreeformValue(e.target.value)}
          onKeyDown={e => {
            // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault()
              handleFreeformSubmit()
            }
          }}
          disabled={disabled}
          block
          autoFocus
        />
      ) : choices.length > 0 ? (
        <ChoiceButtonGroup>
          {choices.map((choice, i) => (
            <ChoiceButton
              key={choice}
              number={i + 1}
              label={<MarkdownRenderer markdown={choice} />}
              showCheckbox={multiSelect}
              checked={selectedChoices.has(choice)}
              isRecommended={choice === recommendedChoice}
              disabled={disabled}
              onClick={() => (multiSelect ? toggleChoice(choice) : onChoose?.(choice))}
            />
          ))}
          {allowFreeform &&
            (showFreeform ? (
              <ChoiceButtonInput
                number={choices.length + 1}
                placeholder="Type your answer..."
                value={freeformValue}
                onChange={setFreeformValue}
                onSubmit={handleFreeformSubmit}
                disabled={disabled}
              />
            ) : (
              <ChoiceButton
                number={choices.length + 1}
                label="Other (type your answer)"
                disabled={disabled}
                onClick={() => setShowFreeform(true)}
              />
            ))}
        </ChoiceButtonGroup>
      ) : null}
    </UserChoiceCard>
  )
}
